import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Spinner } from "@/components/Spinner";
import { ApiPaths, api } from "@/lib/api";
import { isAuthenticated, useLogout, useMe } from "@/lib/auth";
import { tokenStore } from "@/lib/tokenStore";
import type { Membership, OrgInvitePreview, TokenPair } from "@/lib/types";

type Props = { token: string; preview: OrgInvitePreview };

function samePerson(a: string | undefined, b: string | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function AcceptInviteFlow({ token, preview }: Props) {
  const me = useMe();

  if (!isAuthenticated()) {
    return <InviteSignupFlow token={token} preview={preview} />;
  }

  // Signed in, but as WHOM? Until that is known the Accept button is not
  // safe to show — it is only correct for one of the two answers.
  if (me.isLoading) {
    return <Spinner label="" />;
  }

  const currentEmail = me.data?.user?.email;

  // A browser holds one token, so the person reading this invitation is
  // not necessarily the person it was sent to. Reported 2026-10-01: an
  // admin signed in as a partner opened an operator's invite in the same
  // browser. The server already refuses — `accept_invite` compares the
  // accepting user's email to `invited_email` — so nothing could be
  // wrongly joined; what she got was an Accept button that was certain
  // to fail, and an error only afterwards. Say it before the click, and
  // say whose session is in the way.
  //
  // When `me` could not be read at all, fall through to the button
  // rather than block: the server is the authority either way, and a
  // failed identity read is not evidence of a mismatch.
  if (currentEmail && !samePerson(currentEmail, preview.invited_email)) {
    return (
      <WrongAccountNotice
        currentEmail={currentEmail}
        invitedEmail={preview.invited_email}
      />
    );
  }

  return <AcceptInviteButton token={token} />;
}

/**
 * The invitation is for somebody else. Two ways out, and neither of them
 * is the Accept button.
 */
function WrongAccountNotice({
  currentEmail,
  invitedEmail,
}: {
  currentEmail: string;
  invitedEmail: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const logout = useLogout();

  return (
    <div className="space-y-3" data-testid="invite-wrong-account">
      <div className="rounded-md border border-nb-warn/40 bg-nb-warn/10 px-3 py-2.5">
        <p className="text-sm text-nb-text">
          <Trans
            i18nKey="ui.acceptInvite.wrongAccountTitle"
            values={{ current: currentEmail }}
            components={{ strong: <strong /> }}
          />
        </p>
        <p className="mt-1 text-sm text-nb-text-2">
          <Trans
            i18nKey="ui.acceptInvite.wrongAccountBody"
            values={{ invited: invitedEmail }}
            components={{ strong: <strong /> }}
          />
        </p>
      </div>
      <button
        className="btn-primary w-full"
        disabled={logout.isPending}
        data-testid="invite-switch-account"
        onClick={() => {
          // Stay on this page afterwards: with the token gone the
          // invitation re-renders as the sign-in / create-account flow
          // for the right person, so the link they were sent still
          // works instead of dead-ending on the dashboard.
          logout.mutate(undefined, {
            onSettled: () => {
              qc.clear();
              navigate(0);
            },
          });
        }}
      >
        {logout.isPending
          ? t("ui.acceptInvite.switchingAccount")
          : t("ui.acceptInvite.switchAccountBtn", { email: invitedEmail })}
      </button>
      <button
        type="button"
        className="w-full text-xs text-nb-mute underline hover:text-nb-text"
        data-testid="invite-stay-signed-in"
        onClick={() => navigate("/", { replace: true })}
      >
        {t("ui.acceptInvite.staySignedIn", { email: currentEmail })}
      </button>
    </div>
  );
}

/**
 * Brand-new invitee with no account: set a password to create the account
 * and join the inviting org in one step (POST /v1/invites/:token/signup).
 * The email is bound to the signed token server-side, so it's shown but not
 * editable. Signing in (existing account) or provisioning a separate tenant
 * stay available as secondary paths.
 */
function InviteSignupFlow({ token, preview }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Prefill the name the issuing admin already entered on the invite, so
  // the invitee doesn't re-type it (still editable).
  const [firstName, setFirstName] = useState(preview.invited_first_name);
  const [lastName, setLastName] = useState(preview.invited_last_name);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function signup(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr(t("ui.acceptInvite.passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post<{ membership: Membership; tokens: TokenPair }>(
        ApiPaths.inviteSignup(token),
        { password, first_name: firstName, last_name: lastName },
      );
      tokenStore.setPair(res.data.tokens.access, res.data.tokens.refresh);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string; password?: string[] } } })
        ?.response?.data;
      setErr(detail?.detail ?? detail?.password?.[0] ?? t("ui.acceptInvite.createError"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-3" onSubmit={signup}>
      <p className="text-sm text-nb-text">
        <Trans
          i18nKey="ui.acceptInvite.createPrompt"
          values={{ email: preview.invited_email }}
          components={{ strong: <strong /> }}
        />
      </p>
      <div className="flex gap-2">
        <input
          className="input flex-1"
          placeholder={t("ui.acceptInvite.firstNamePlaceholder")}
          autoComplete="given-name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <input
          className="input flex-1"
          placeholder={t("ui.acceptInvite.lastNamePlaceholder")}
          autoComplete="family-name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
      </div>
      <input
        className="input w-full"
        type="password"
        placeholder={t("ui.acceptInvite.passwordPlaceholder")}
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />
      <input
        className="input w-full"
        type="password"
        placeholder={t("ui.acceptInvite.confirmPlaceholder")}
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        required
      />
      <button className="btn-primary w-full" type="submit" disabled={busy}>
        {busy ? t("ui.acceptInvite.creating") : t("ui.acceptInvite.createBtn")}
      </button>
      {err && (
        <p role="alert" className="text-sm text-cvp-danger">
          {err}
        </p>
      )}
      {/* `/invites/` — plural. These built `/invite/<token>`, a path no
          route matches, so signing in from an invitation landed on a
          dead URL and the invitation was lost. Harmless while nothing
          honoured `?next=`; not harmless now that /login does. */}
      <div className="flex justify-between text-xs text-nb-mute">
        <button
          type="button"
          className="underline hover:text-nb-text"
          onClick={() =>
            navigate(`/login?next=${encodeURIComponent(`/invites/${token}`)}`)
          }
        >
          {t("ui.acceptInvite.signInLink")}
        </button>
        <button
          type="button"
          className="underline hover:text-nb-text"
          onClick={() =>
            navigate(`/signup?next=${encodeURIComponent(`/invites/${token}`)}`)
          }
        >
          {t("ui.acceptInvite.provisionLink")}
        </button>
      </div>
    </form>
  );
}

/** Authenticated user accepts the invite for their current account. */
function AcceptInviteButton({ token }: { token: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function accept() {
    setErr(null);
    setBusy(true);
    try {
      const res = await api.post<{ membership: Membership; tokens: TokenPair }>(
        ApiPaths.authInviteAccept(token),
      );
      tokenStore.setPair(res.data.tokens.access, res.data.tokens.refresh);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErr(detail ?? t("ui.acceptInvite.acceptError"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <button className="btn-primary w-full" onClick={accept} disabled={busy}>
        {busy ? t("ui.acceptInvite.accepting") : t("ui.acceptInvite.acceptBtn")}
      </button>
      {err && (
        <p role="alert" className="text-sm text-cvp-danger">
          {err}
        </p>
      )}
    </div>
  );
}
